import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InterfaceScroller, InterfaceStyles } from "../Components/Interface";
import { SiteSyles, useIsVisible, useSite } from "../Context/SiteContext";
import { DrawOnText } from "../Landing/Landing";
import { createContext, CSSProperties, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { faExpand, faPhotoFilm, faTimes } from "@fortawesome/free-solid-svg-icons";

const GalleryContext = createContext<{
    fullscreenSource:{get?:{src:string,type:'image' | 'video'},set?:Dispatch<SetStateAction<{src:string,type:'image' | 'video'} | undefined>>},
}>({
    fullscreenSource:{},
});

const useGallery = () => useContext(GalleryContext);

export const Gallery = () => {

    const Site = useSite();

    const [fullscreenSource, setIsFullscreenSource] = useState<{src:string,type:'image' | 'video'} | undefined>();

    const pageRef = useRef<HTMLDivElement>(null);

    useEffect(()=>{
        Site.pageRef = pageRef;
    },[]);

    return(
        <GalleryContext.Provider value={{
            fullscreenSource:{get:fullscreenSource,set:setIsFullscreenSource},
        }}>
            <div
                ref={pageRef}
                style={{
                    display:'flex',
                    position:'relative',
                    height:'100vh',
                    width:'100vw',
                    overflow:'hidden',
                    backgroundColor:'#bbb',
                    boxSizing:'border-box',
                }}
            >
                <InterfaceScroller scrollerStyle={{padding: Site.isMobile ? 20 : 50,gap: Site.isMobile ? 20 : 50,}}>
                    <SavantSixBanner></SavantSixBanner>
                    <SavantSixImages></SavantSixImages>
                    <ProvenceBanner></ProvenceBanner>
                    <ProvenceUI></ProvenceUI>
                    <DBD></DBD>
                    <Shield></Shield>
                    <Bria></Bria>
                    <Vagabond></Vagabond>
                    <RansackedBanner></RansackedBanner>
                    <Than></Than>
                    <Salahan></Salahan>
                </InterfaceScroller>
                <div style={{
                    position:'absolute',
                    top:0,
                    right:0,
                    width:40,
                    height:40,
                    ...SiteSyles.center,
                    cursor:'pointer',
                }} onClick={()=>{
                    window.open('/','_self');
                }}>
                    <FontAwesomeIcon icon={faTimes} style={{color:'#fff'}}></FontAwesomeIcon>
                </div>
                <FullscreenItem></FullscreenItem>
            </div>
        </GalleryContext.Provider>
    );
};

const GalleryPage = (props:{title?:string, style?:CSSProperties, children?:ReactNode}) => {

    const Site = useSite();

    return(
        <div style={{
            display:'flex',
            position:'relative',
            flexDirection:'column',
            backgroundColor:'#000',
            padding: 20,
            minHeight:`calc(100vh - ${Site.isMobile ? 40 : 100}px)`,
            boxSizing:'border-box',
            gap:20,
            overflow:'hidden',
            ...props.style,
        }}>
            {props.children}
            <div style={{
                position:'absolute',
                ...SiteSyles.text,
                top: 30,
                left:30,
                color:'#fff',
                textShadow:'1px 1px 2px #000',
                userSelect:'none',
                pointerEvents:'none'
            }}>{`Michael Yull ${props.title ?? ''}`}</div>
        </div>
    );

};

const GalleryRow = (props:any) => {

    const Site = useSite();

    return(
        <div {...props} style={{
            display:'flex',
            flex:1,
            gap: 20,
            flexDirection: Site.isMobile ? 'column' : 'row',
            overflow:'hidden',
        }}></div>
    );
};

const GalleryItem = (props:{type? : 'image' | 'video', src:string, style?:CSSProperties}) => {

    const Gallery = useGallery();

    const itemStyle = {
        height:'100%',
        width:'100%',
        objectFit:'cover',
        userSelect:'none',
        pointerEvents: props.type === 'video' ? undefined : 'none',
        ...props.style,
    } as CSSProperties;

    const videoRef = useRef<HTMLVideoElement>(null);

    const isVisible = useIsVisible(videoRef);

    useEffect(()=>{
        if(videoRef.current){
            isVisible ? videoRef.current.play() : videoRef.current.pause();
        }
    },[isVisible]);
    
    return(
        <div style={{
            display:'flex',
            flex:1,            
            overflow:'hidden',
        }} onClick={()=>{
            console.log(props.type)
            props.type !== 'video' && Gallery.fullscreenSource.set!({src:props.src,type:props.type ?? 'image'});
        }}>
            {props.type === 'video' ? (
                <video ref={videoRef} src={props.src} style={itemStyle} playsInline autoPlay muted controls loop></video>
            ) : (
                <img src={props.src} style={itemStyle}></img>
            )}
        </div>
    );
};

const FullscreenItem = () => {

    const Gallery = useGallery();

    const [show, setShow] = useState(false);
    const [source, setSource] = useState<{src:string,type:'image' | 'video'}>();

    useEffect(()=>{
        Gallery.fullscreenSource.get ? setShow(true) : setTimeout(()=>setShow(false),500);
        Gallery.fullscreenSource.get && setSource(Gallery.fullscreenSource.get);
    },[Gallery.fullscreenSource.get]);

    const itemStyle = {
        height:'100%',
        width:'100%',
        objectFit:'contain',
        userSelect:'none',
        pointerEvents:'none',
    } as CSSProperties;
    
    return(
        <div style={{
            display: show ? 'flex' : 'none',
            position:'absolute',
            inset:0,
            opacity : Gallery.fullscreenSource.get !== undefined ?  1 : 0,
            transition:'opacity 0.5s',
            backgroundColor:"#0008",
        }} onClick={()=>{
            Gallery.fullscreenSource.set!(undefined);
        }}>
            <div style={{
                display:'flex',
                flex:1,
                position:'relative',
                overflow:'hidden',
                padding:20,
                boxSizing:'border-box',
            }}>
                {source && source.type === 'image' ? (
                    <img src={source?.src} style={itemStyle}></img>
                ) : (
                    <video src={source?.src} style={itemStyle} playsInline controls preload="auto"></video>
                )}
            </div>
        </div>
    );

};

const SavantSixImages = () => {

    return(
        <GalleryPage title="- 3D Design | Art Direction">
            <GalleryRow>
                <GalleryItem src="assets/gallery/sg6-cover.png"></GalleryItem>
                <GalleryItem src="assets/gallery/sg6-wishbone.png"></GalleryItem>
            </GalleryRow>
            <GalleryRow>
                <GalleryItem src="assets/gallery/sg6-rest.png"></GalleryItem>
                <GalleryItem src="assets/gallery/sg6-cinema.png"></GalleryItem>
            </GalleryRow>
            <GalleryRow>
                <GalleryItem src="assets/gallery/sg6-wistful.png"></GalleryItem>
                <GalleryItem src="assets/gallery/sg6-wolf.png"></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const Vagabond = () => {

    return(
        <GalleryPage title="- Beer Label Illustration">
            <GalleryRow>
                <GalleryItem src="assets/gallery/vagabond-temple.png" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const DBD = () => {

    return(
        <GalleryPage title="- Logo Design">
            <GalleryRow>
                <GalleryItem src="assets/gallery/dbd.png" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const Shield = () => {

    return(
        <GalleryPage title="- 3D Model">
            <GalleryRow>
                <GalleryItem src="assets/gallery/shield-model.png" style={{objectFit:'contain'}}></GalleryItem>
                <GalleryItem src="assets/gallery/shield-model-back.png" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const RansackedBanner = () => {

    return(
        <GalleryPage title="- Character Sheet">
            <GalleryRow>
                <GalleryItem src="assets/gallery/ransacked-banner.png" style={{objectFit:'cover'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const Bria = () => {

    return(
        <GalleryPage title="- 2D/3D Animation">
            <GalleryRow>
                <GalleryItem type="video" src="assets/gallery/bria-anim.mp4" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const Than = () => {

    return(
        <GalleryPage title="- Character Commission">
            <GalleryRow>
                <GalleryItem type="image" src="assets/gallery/than-01.png" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const SavantSixBanner = () => {

    return(
        <GalleryPage title="- Promotional Trailer - 3D Design | Art Direction">
            <GalleryRow>
                <GalleryItem type="video" src="assets/savant-garde/savant-garde-trailer.mp4" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const ProvenceBanner = () => {

    return(
        <GalleryPage title="- Promotional Videos - Motion Graphics | Art Direction | Software Engineering">
            <GalleryRow>
                <GalleryItem type="video" src="assets/provence/provence-banner-1.mp4" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const ProvenceUI = () => {

    return(
        <GalleryPage title="- UI/UX Design | Software Engineering">
            <GalleryRow>
                <GalleryItem type="image" src="assets/gallery/provence-ui.png" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};

const Salahan = () => {

    return(
        <GalleryPage title="- Book Illustration Commission">
            <GalleryRow>
                <GalleryItem type="image" src="assets/gallery/salahan-map.png" style={{objectFit:'contain'}}></GalleryItem>
            </GalleryRow>
        </GalleryPage>
    );

};